// src/store/images.js
// Модуль Vuex раздела «Генерация изображений»: настройки, баланс, задачи, сессия, опрос очереди
import {
    getMoscowDateKey,
    getNextMoscowMidnightMs,
    getSessionKey,
    loadSessionClearMode,
    saveSessionClearMode,
} from '@/utils/images/session'

// Сервис грузим лениво: http-common сам импортирует store (циклическая зависимость)
const api = () => import('@/services/ImagesService').then(m => m.default)

const POLL_INTERVAL_MS = 2000
const ACTIVE_STATUSES = ['queued', 'processing']
export const MODES = ['photo', 'drawing', 'angles', 'free', 'catalog']

let pollTimer = null
let pollBusy = false
let midnightTimer = null
let initPromise = null

// id пользователя из сохранённой авторизации (state создаётся раньше, чем восстановлен rootState.user)
const rootUserId = () => {
    try { return JSON.parse(localStorage.getItem('user') || 'null')?.id ?? null } catch (e) { return null }
}

const byCreatedDesc = (a, b) => (b.createdAt || 0) - (a.createdAt || 0)

const localId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
    return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
}

// Задача принадлежит текущему пользователю (author пустой — считаем своей)
const isOwn = (job, user) => !job.author || !user || String(job.author.id) === String(user.id)

const emptyStaged = () => MODES.reduce((acc, m) => { acc[m] = []; return acc }, {})

export default {
    namespaced: true,

    state: () => {
        // Пользователь может быть ещё не известен — режим уточняется в syncSessionUser
        const userId = rootUserId()
        const mode = loadSessionClearMode(userId)
        const day = getMoscowDateKey()
        return {
            hasApiKey: false,
            provider: null,
            settingsLoaded: false,
            balance: { amount: 0, approxGenerations: 0, loaded: false },
            jobs: {},            // id -> Job (с сервера)
            jobsLoaded: false,
            staged: emptyStaged(), // mode -> [карточка до генерации]
            sessionClearMode: mode,
            moscowDayKey: day,
            sessionKey: getSessionKey(mode, day, userId),
            sessionUserId: userId,
            backgrounds: [],     // библиотека фонов (П3), createdAt убыв.
            backgroundsLoaded: false,
        }
    },

    getters: {
        jobsList: (state) => Object.values(state.jobs).sort(byCreatedDesc),

        // «Текущая сессия» режима: свой sessionKey или без ключа и не done
        jobsByMode: (state, getters) => (mode) => getters.jobsList.filter(j =>
            j.mode === mode && (j.sessionKey === state.sessionKey || (!j.sessionKey && j.status !== 'done'))
        ),

        // История: все готовые задачи независимо от сессии
        doneJobs: (state, getters) => getters.jobsList.filter(j => j.status === 'done'),

        stagedByMode: (state) => (mode) => state.staged[mode] || [],

        activeJobIds: (state) => Object.values(state.jobs)
            .filter(j => ACTIVE_STATUSES.includes(j.status))
            .map(j => j.id),

        jobById: (state) => (id) => state.jobs[id] || null,
    },

    mutations: {
        SET_SETTINGS(state, { hasApiKey, provider }) {
            state.hasApiKey = !!hasApiKey
            if (provider !== undefined) state.provider = provider
            state.settingsLoaded = true
        },
        SET_HAS_API_KEY(state, value) {
            state.hasApiKey = !!value
        },
        SET_BALANCE(state, balance) {
            state.balance = { ...state.balance, ...balance }
        },
        SET_JOBS(state, jobs) {
            const map = {}
            jobs.forEach(j => { map[j.id] = j })
            state.jobs = map
            state.jobsLoaded = true
        },
        UPSERT_JOBS(state, jobs) {
            jobs.forEach(j => {
                if (j && j.id) state.jobs[j.id] = { ...(state.jobs[j.id] || {}), ...j }
            })
        },
        REMOVE_JOBS(state, ids) {
            ids.forEach(id => { delete state.jobs[id] })
        },
        ADD_STAGED(state, { mode, items }) {
            if (!state.staged[mode]) state.staged[mode] = []
            state.staged[mode].push(...items)
        },
        UPDATE_STAGED(state, { mode, id, patch }) {
            const list = state.staged[mode] || []
            const idx = list.findIndex(s => s.id === id)
            if (idx >= 0) list[idx] = { ...list[idx], ...patch }
        },
        REMOVE_STAGED(state, { mode, ids }) {
            if (!state.staged[mode]) return
            const set = new Set(ids)
            state.staged[mode] = state.staged[mode].filter(s => !set.has(s.id))
        },
        CLEAR_STAGED(state, mode) {
            state.staged[mode] = []
        },
        SET_BACKGROUNDS(state, list) {
            state.backgrounds = list
            state.backgroundsLoaded = true
        },
        UPSERT_BACKGROUND(state, bg) {
            const idx = state.backgrounds.findIndex(b => b.id === bg.id)
            if (idx >= 0) state.backgrounds[idx] = bg
            else state.backgrounds.unshift(bg)
        },
        REMOVE_BACKGROUND(state, id) {
            state.backgrounds = state.backgrounds.filter(b => b.id !== id)
        },
        SET_SESSION(state, { mode, day, userId }) {
            if (userId !== undefined) state.sessionUserId = userId
            if (mode) state.sessionClearMode = mode
            if (day) state.moscowDayKey = day
            state.sessionKey = getSessionKey(state.sessionClearMode, state.moscowDayKey, state.sessionUserId)
        },
    },

    actions: {
        // Однократная инициализация раздела (вызывается из обёртки Images.vue)
        init({ dispatch }) {
            dispatch('syncSessionUser')
            if (!initPromise) {
                initPromise = Promise.all([
                    dispatch('refreshSettings'),
                    dispatch('loadJobs'),
                ]).catch((e) => {
                    initPromise = null
                    throw e
                })
                dispatch('scheduleMidnight')
            }
            return initPromise
        },

        // Режим и ключ сессии текущего пользователя (при входе в раздел и после перелогина)
        syncSessionUser({ state, commit, dispatch, rootState }) {
            const userId = rootState.user?.id ?? null
            if (userId === state.sessionUserId) return
            commit('SET_SESSION', { userId, mode: loadSessionClearMode(userId), day: getMoscowDateKey() })
            dispatch('scheduleMidnight')
        },

        async refreshSettings({ commit, dispatch }) {
            const svc = await api()
            const res = await svc.getSettings()
            commit('SET_SETTINGS', { hasApiKey: res.data.hasApiKeyNanoBananaPro, provider: res.data.provider })
            if (res.data.hasApiKeyNanoBananaPro) dispatch('refreshBalance')
        },

        async refreshBalance({ commit }) {
            try {
                const svc = await api()
                const res = await svc.getBalance()
                const d = res.data || {}
                if (!d.ok) {
                    console.warn('Баланс недоступен', d)
                    return
                }
                commit('SET_BALANCE', {
                    amount: Math.max(0, Number(d.amount) || 0),
                    approxGenerations: Math.max(0, Math.floor(Number(d.approxGenerations) || 0)),
                    loaded: true,
                })
            } catch (e) {
                console.warn('Ошибка загрузки баланса', e)
            }
        },

        // Пустой ключ = удалить. Ошибка пробрасывается вызывающему
        async saveApiKey({ commit, dispatch }, apiKey) {
            const svc = await api()
            const res = await svc.saveApiKey(String(apiKey || '').trim())
            commit('SET_HAS_API_KEY', res.data.hasApiKeyNanoBananaPro)
            await dispatch('refreshBalance')
            return res.data
        },

        async deleteApiKey({ commit }) {
            const svc = await api()
            await svc.deleteApiKey()
            commit('SET_HAS_API_KEY', false)
            commit('SET_BALANCE', { amount: 0, approxGenerations: 0, loaded: false })
        },

        // Без параметров — полная загрузка с заменой; с параметрами — догрузка
        async loadJobs({ commit, dispatch }, params) {
            const svc = await api()
            const res = await svc.getJobs(params || {})
            const jobs = res.data.jobs || []
            if (params) commit('UPSERT_JOBS', jobs)
            else {
                commit('SET_JOBS', jobs)
                await dispatch('cleanupOutsideSession')
            }
            dispatch('ensurePolling')
            return jobs
        },

        // Библиотека фонов. Ошибки пробрасываются вызывающему
        async loadBackgrounds({ commit }) {
            const svc = await api()
            const res = await svc.getBackgrounds()
            const list = res.data.backgrounds || []
            commit('SET_BACKGROUNDS', list)
            return list
        },

        // { name, dataUrl } -> сохранённый Background
        async addBackground({ commit }, data) {
            const svc = await api()
            const res = await svc.uploadBackground(data)
            commit('UPSERT_BACKGROUND', res.data.background)
            return res.data.background
        },

        async renameBackground({ commit }, { id, name }) {
            const svc = await api()
            const res = await svc.renameBackground(id, name)
            commit('UPSERT_BACKGROUND', res.data.background)
            return res.data.background
        },

        async removeBackground({ commit }, id) {
            const svc = await api()
            await svc.deleteBackground(id)
            commit('REMOVE_BACKGROUND', id)
        },

        upsertJobs({ commit, dispatch }, jobs) {
            commit('UPSERT_JOBS', Array.isArray(jobs) ? jobs : [jobs])
            dispatch('ensurePolling')
        },

        removeJobs({ commit }, ids) {
            commit('REMOVE_JOBS', Array.isArray(ids) ? ids : [ids])
        },

        // Удаление на сервере и локально
        async deleteJobs({ commit }, ids) {
            const list = Array.isArray(ids) ? ids : [ids]
            if (!list.length) return
            const svc = await api()
            if (list.length === 1) await svc.deleteJob(list[0])
            else await svc.deleteJobs(list)
            commit('REMOVE_JOBS', list)
        },

        // Флаг выбора (оптимистично, на сервер — PUT /jobs/:id)
        async setSelected({ commit, state }, { ids, selected }) {
            const list = (Array.isArray(ids) ? ids : [ids]).filter(id => state.jobs[id])
            commit('UPSERT_JOBS', list.map(id => ({ id, selected: !!selected })))
            const svc = await api()
            await Promise.all(list.map(id => svc.updateJob(id, { selected: !!selected }).catch(e => {
                console.warn('Не удалось сохранить выбор', e)
            })))
        },

        toggleSelected({ dispatch, state }, id) {
            const job = state.jobs[id]
            if (job) return dispatch('setSelected', { ids: [id], selected: !job.selected })
        },

        // Постановка генерации. kind: 'image' | 'angles' | 'refineAngle' | 'maskEdit' | 'free' | 'catalog'. Возвращает job
        async generate({ dispatch, state }, { kind, payload }) {
            const svc = await api()
            const body = { sessionKey: state.sessionKey, ...payload }
            let res
            if (kind === 'angles') res = await svc.generateAngles(body)
            else if (kind === 'refineAngle') res = await svc.refineAngle(body)
            else if (kind === 'maskEdit') res = await svc.generateMaskEdit(body)
            else if (kind === 'free') res = await svc.generateFree(body)
            else if (kind === 'catalog') res = await svc.generateCatalog(body)
            else res = await svc.generateImage(body)
            const job = res.data.job
            await dispatch('upsertJobs', [job])
            return job
        },

        // Опрос очереди раз в 2 с, пока есть queued/processing
        ensurePolling({ getters, dispatch }) {
            if (!getters.activeJobIds.length) {
                dispatch('stopPolling')
                return
            }
            if (pollTimer) return
            pollTimer = setInterval(() => dispatch('pollOnce'), POLL_INTERVAL_MS)
        },

        stopPolling() {
            if (pollTimer) clearInterval(pollTimer)
            pollTimer = null
        },

        async pollOnce({ getters, commit, dispatch, state }) {
            if (pollBusy) return
            const ids = getters.activeJobIds
            if (!ids.length) {
                dispatch('stopPolling')
                return
            }
            pollBusy = true
            try {
                const svc = await api()
                const res = await svc.getJobs({ ids })
                const jobs = res.data.jobs || []
                const finished = jobs.some(j => !ACTIVE_STATUSES.includes(j.status)
                    && ACTIVE_STATUSES.includes(state.jobs[j.id]?.status))
                // Задачи, которых больше нет на сервере, убираем
                const returned = new Set(jobs.map(j => j.id))
                const gone = ids.filter(id => !returned.has(id))
                if (gone.length) commit('REMOVE_JOBS', gone)
                commit('UPSERT_JOBS', jobs)
                if (finished) dispatch('refreshBalance')
            } catch (e) {
                console.warn('Ошибка опроса задач', e)
            } finally {
                pollBusy = false
                dispatch('ensurePolling')
            }
        },

        // Чужие сессии: свои незавершённые с ошибкой удаляем, задачам без ключа ставим текущий.
        // Идущие (queued/processing) не трогаем — после готовности они попадут в Историю.
        async cleanupOutsideSession({ state, commit, rootGetters }) {
            const user = rootGetters.currentUser
            const key = state.sessionKey
            const toDelete = []
            const toRebind = []
            Object.values(state.jobs).forEach(j => {
                if (!isOwn(j, user) || j.status === 'done') return
                if (!j.sessionKey) toRebind.push(j.id)
                else if (j.sessionKey !== key && j.status === 'error') toDelete.push(j.id)
            })
            if (!toDelete.length && !toRebind.length) return
            const svc = await api()
            try {
                if (toRebind.length) {
                    await Promise.all(toRebind.map(id => svc.updateJob(id, { sessionKey: key })))
                    commit('UPSERT_JOBS', toRebind.map(id => ({ id, sessionKey: key })))
                }
                if (toDelete.length) {
                    await svc.deleteJobs(toDelete)
                    commit('REMOVE_JOBS', toDelete)
                }
            } catch (e) {
                console.error('Очистка сессии не удалась', e)
            }
        },

        // Смена режима очистки: видимая сессия переезжает на новый ключ без потери карточек
        async setSessionClearMode({ state, commit, dispatch, rootGetters }, mode) {
            const next = mode === 'daily' ? 'daily' : 'onClose'
            if (next === state.sessionClearMode) return
            const prevKey = state.sessionKey
            saveSessionClearMode(next, state.sessionUserId)
            commit('SET_SESSION', { mode: next, day: getMoscowDateKey() })
            dispatch('scheduleMidnight')
            const newKey = state.sessionKey
            if (prevKey === newKey) return
            const user = rootGetters.currentUser
            const ids = Object.values(state.jobs)
                .filter(j => isOwn(j, user) && (j.sessionKey === prevKey || (!j.sessionKey && j.status !== 'done')))
                .map(j => j.id)
            if (!ids.length) return
            commit('UPSERT_JOBS', ids.map(id => ({ id, sessionKey: newKey })))
            try {
                const svc = await api()
                await Promise.all(ids.map(id => svc.updateJob(id, { sessionKey: newKey })))
            } catch (e) {
                console.error('Перенос сессии не удался', e)
            }
        },

        // В режиме 'daily' — смена ключа в 00:00 по Москве, пока вкладка открыта
        scheduleMidnight({ state, commit, dispatch }) {
            if (midnightTimer) clearTimeout(midnightTimer)
            midnightTimer = null
            if (state.sessionClearMode !== 'daily') return
            const now = new Date()
            const delay = Math.max(1000, getNextMoscowMidnightMs(now) - now.getTime() + 250)
            midnightTimer = setTimeout(() => {
                commit('SET_SESSION', { day: getMoscowDateKey(new Date()) })
                dispatch('cleanupOutsideSession')
                dispatch('scheduleMidnight')
            }, delay)
        },

        // Staged-карточки (только на фронте, до «Сгенерировать»).
        // item: { originalName, fileSize, previewUrl, file?, ...любые поля } -> добавляется id, mode, status, createdAt
        addStaged({ commit }, { mode, items }) {
            const now = Date.now()
            const prepared = (Array.isArray(items) ? items : [items]).map((it, i) => ({
                id: localId(),
                mode,
                status: 'staged',
                createdAt: now + i,
                ...it,
            }))
            commit('ADD_STAGED', { mode, items: prepared })
            return prepared
        },

        updateStaged({ commit }, { mode, id, patch }) {
            commit('UPDATE_STAGED', { mode, id, patch })
        },

        removeStaged({ commit }, { mode, ids }) {
            commit('REMOVE_STAGED', { mode, ids: Array.isArray(ids) ? ids : [ids] })
        },

        clearStaged({ commit }, mode) {
            commit('CLEAR_STAGED', mode)
        },
    },
}
